import { Form, useActionData, useNavigation } from "react-router";
import { MailCheck } from "lucide-react";
import type { Me } from "@repo/shared-types";
import { ErrorNote, PasswordField, PasswordManagerUsername } from "@/components/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AccountPage, type ActionResult } from "@/routes/account-section";
import { useSession } from "@/routes/root";

/**
 * Changing the password, with the current one — or, for an account that signs
 * in with Google and has none, a link to set a first (ADR 0068: the button
 * says so, with nothing around it).
 */

export default function AccountPassword() {
  const session = useSession();
  // The loader let the page draw only with a session.
  return session && <Password session={session} />;
}

function Password({ session }: { session: Me }) {
  const answer = useActionData() as ActionResult | undefined;
  const result = answer?.intent === "password" ? answer : undefined;
  const busy = useNavigation().state === "submitting";
  const { email, hasPassword } = session.user;
  const fieldError = (name: string) => (result?.field === name ? result.error : undefined);
  const formWide = result?.error && !result.field ? result.error : undefined;

  return (
    <AccountPage title="Password">
      {hasPassword ? (
        <Form method="post" key={result?.changedAt ?? 0} className="space-y-4">
          <input type="hidden" name="intent" value="password" />
          <PasswordManagerUsername email={email} />
          <PasswordField
            label="Current password"
            name="currentPassword"
            autoComplete="current-password"
            checkLength={false}
            error={fieldError("currentPassword")}
          />
          <PasswordField
            label="New password"
            name="newPassword"
            autoComplete="new-password"
            error={fieldError("newPassword")}
            hint="At least 8 characters."
          />
          {formWide && <ErrorNote message={formWide} />}
          <Button type="submit" size="lg" disabled={busy} className="w-full">
            Change password
          </Button>
        </Form>
      ) : (
        <Form method="post" className="space-y-4">
          <input type="hidden" name="intent" value="set-password" />
          <input type="hidden" name="email" value={email} />
          {formWide && <ErrorNote message={formWide} />}
          {result?.note && (
            <Alert role="status" className="border-transparent bg-success-muted text-success">
              <MailCheck />
              <AlertDescription className="text-foreground">{result.note}</AlertDescription>
            </Alert>
          )}
          <Button type="submit" size="lg" disabled={busy} className="w-full">
            Email me a link to set a password
          </Button>
        </Form>
      )}
    </AccountPage>
  );
}
